/* eslint-disable import/no-duplicates */
"use client";

import { faCalendar, faCircleXmark, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isValid } from "date-fns";
import { startOfYear } from "date-fns";
import { ja } from "date-fns/locale";
import React from "react";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import { UseFormResetField } from "react-hook-form";

import "react-datepicker/dist/react-datepicker.css";

registerLocale("ja", ja);

import { dateTimeFormat } from "constants/datetime";
import { cn } from "utils/cn";
import { formatDate } from "utils/formatDatetime";

interface IEventSearch {
  onChange: (...event: unknown[]) => void;
  handleSearch?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resetField?: UseFormResetField<any>;
}

type Props = {
  value: string | Date;
  placeholder?: string;
  dateFormat?: string;
  valueFormat?: string;
  dateFormatCalendar?: string;
  minDate?: Date;
  maxDate?: Date;
  isError?: boolean;
  isDisabled?: boolean;
  hasClearValue?: boolean;
  onBlur?: (...event: unknown[]) => void;
  icon?: IconDefinition;
  className?: string;
} & IEventSearch;

const DatepickerInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ ...props }, ref) => <input type="text" {...props} ref={ref} readOnly />
);
DatepickerInput.displayName = "DatepickerInput";

const DatePicker = ({
  value,
  placeholder = "指定なし",
  dateFormat = dateTimeFormat.DEFAULT,
  valueFormat = dateTimeFormat.DEFAULT,
  dateFormatCalendar = dateTimeFormat.YEAR_MONTH_JP,
  isError,
  isDisabled,
  minDate = startOfYear(new Date(1900, 0, 1)),
  maxDate,
  hasClearValue = true,
  onChange,
  onBlur: formBlur,
  icon = faCalendar,
  className,
}: Props) => {
  const handleChange = (value: Date) => {
    onChange(formatDate(value, valueFormat));
  };

  const colorClasses = isDisabled
    ? "bg-disabled text-gray border-form-border"
    : isError
    ? "bg-[#fdf2e1] border-attention"
    : "bg-input-normal border-input-normal";

  return (
    <div className={cn("relative w-[160px] rounded bg-input-normal focus:shadow-input [&>div]:w-full", className)}>
      <ReactDatePicker
        locale="ja"
        showIcon
        showMonthDropdown
        showYearDropdown
        scrollableYearDropdown
        fixedHeight
        yearDropdownItemNumber={100}
        dateFormatCalendar={dateFormatCalendar}
        dateFormat={dateFormat}
        className={cn(
          "flex h-12 w-full cursor-pointer rounded-[4px] border pl-6 outline-none placeholder:text-black-light",
          colorClasses
        )}
        customInput={<DatepickerInput />}
        placeholderText={placeholder}
        selected={isValid(new Date(value)) ? new Date(value) : null}
        onChange={(date: Date | null) => {
          if (date) handleChange(date);
        }}
        minDate={minDate}
        maxDate={maxDate}
        disabled={isDisabled}
        onCalendarClose={() => formBlur && formBlur()} // trigger validation
        onKeyDown={(e) => {
          e.preventDefault();
        }}
        onChangeRaw={(e) => {
          if (e) e.preventDefault();
        }}
      />

      <FontAwesomeIcon
        className="absolute right-3 top-1/2 -translate-y-1/2 transform cursor-pointer text-gray"
        icon={hasClearValue && value ? faCircleXmark : icon}
        {...(hasClearValue
          ? {
              onClick: () => {
                onChange("");
                if (formBlur) formBlur();
              },
            }
          : {})}
      />
    </div>
  );
};

export default DatePicker;
